import { supabase } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';

/**
 * Utility for tracking Progressive Web App (PWA) installations
 */

// Event names for tracking
export const PWA_EVENTS = {
  INSTALLED: 'app_installed',
  INSTALLATION_PROMPT_SHOWN: 'app_install_prompt_shown',
  INSTALLATION_ACCEPTED: 'app_install_accepted',
  INSTALLATION_REJECTED: 'app_install_rejected',
};

/**
 * Gets or generates a unique device ID stored in localStorage.
 * @returns {string} The device ID.
 */
const getDeviceId = (): string => {
  let deviceId = localStorage.getItem('pwa_device_id');
  if (!deviceId) {
    deviceId = uuidv4();
    localStorage.setItem('pwa_device_id', deviceId);
  }
  return deviceId;
};

/**
 * Track an installation event by sending it to Supabase.
 * @param eventName The name of the event to track
 * @param eventData Optional additional data about the event
 * @param userId Optional user ID if the user is authenticated
 */
export const trackPwaEvent = async (
  eventName: string,
  eventData?: Record<string, any>,
  userId?: string | null
) => {
  try {
    const deviceId = getDeviceId();
    const { data, error } = await supabase
      .from('pwa_install_events')
      .insert([
        {
          user_id: userId || null,
          device_id: deviceId,
          event_name: eventName,
          event_data: eventData,
        },
      ]);

    if (error) {
      console.error('Error tracking PWA event to Supabase:', error);
      // Fallback to local storage if Supabase fails? For now, just log.
    }

    // Log event to console in development mode
    if (import.meta.env.DEV) {
      console.log('PWA Event tracked to Supabase:', eventName, eventData, 'DeviceID:', deviceId, 'UserID:', userId);
    }

    // If Google Analytics is available, track the event there too
    if (window.gtag) {
      window.gtag('event', eventName, { ...eventData, device_id: deviceId });
    }
  } catch (error) {
    console.error('Error in trackPwaEvent function:', error);
  }
};

/**
 * Check if the app is currently running in installed mode
 */
export const isAppInstalled = (): boolean => {
  // Check for standalone display mode
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  // Check for navigator.standalone (iOS)
  const isNavigatorStandalone = (window.navigator as any).standalone === true;
  // Check if 'appInstalled' flag is set from a previous successful installation this session/browser
  const localInstallFlag = localStorage.getItem('appInstalled') === 'true';

  return isStandalone || isNavigatorStandalone || localInstallFlag;
};

/**
 * Get statistics about PWA installations from Supabase.
 * This function is intended for admin use.
 */
export const getPwaInstallStats = async () => {
  try {
    // Get total installations
    const { count: totalInstalls, error: countError } = await supabase
      .from('pwa_install_events')
      .select('*', { count: 'exact', head: true })
      .eq('event_name', PWA_EVENTS.INSTALLED);

    if (countError) throw countError;

    // Get the last installation timestamp
    const { data: lastInstallData, error: lastInstallError } = await supabase
      .from('pwa_install_events')
      .select('created_at')
      .eq('event_name', PWA_EVENTS.INSTALLED)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (lastInstallError) throw lastInstallError;

    // Get recent events (e.g., last 10)
    const { data: recentEvents, error: eventsError } = await supabase
      .from('pwa_install_events')
      .select('event_name, created_at, user_id, device_id, event_data')
      .order('created_at', { ascending: false })
      .limit(10);

    if (eventsError) throw eventsError;
    
    // Get distinct device install count
    // Cast function name to 'any' to bypass TS check if types are not yet updated
    const { data: distinctDevices, error: distinctDevicesError } = await supabase
      .rpc('count_distinct_installed_devices' as any); 

    if (distinctDevicesError) {
        console.warn('Could not fetch distinct device count. RPC "count_distinct_installed_devices" might be missing or failed.', distinctDevicesError);
    }

    // The RPC function 'count_distinct_installed_devices' returns an array with one object: [{ count: N }]
    // So we need to access the first element and then its count property.
    const distinctDeviceInstallsCount = Array.isArray(distinctDevices) && distinctDevices.length > 0 && typeof distinctDevices[0].count === 'number' 
                                        ? distinctDevices[0].count 
                                        : 0;

    return {
      totalInstalls: totalInstalls || 0,
      distinctDeviceInstalls: distinctDeviceInstallsCount,
      lastInstalled: lastInstallData?.created_at || null,
      events: recentEvents || [],
    };
  } catch (error) {
    console.error('Error getting PWA install stats from Supabase:', error);
    return { totalInstalls: 0, distinctDeviceInstalls: 0, lastInstalled: null, events: [] };
  }
};

// Helper function to ensure device ID is generated on app load
getDeviceId();
